import { Link } from 'react-router-dom';
import { getVerifyState } from '../../lib/verify-states.js';

/**
 * Scan stats sidebar panel.
 *
 * Reads the full response from GET /api/dashboard/scans/stats (post-patches):
 *   { total, last_24h, last_7d, last_30d, unique_staff, top_staff[], by_result }
 *
 * Laid out as three sections stacked vertically:
 *   1. Timeline — 24h / 7d / 30d counts as small cells
 *   2. Result breakdown — verified / under_review / no_longer_employed / unknown
 *      with mini progress bars showing proportion
 *   3. Top staff — Randy's `top_staff[]` array, ranked by scan_count
 *
 * The sidebar is sticky on desktop, stacks below the table on mobile.
 */
export function ScanStatsPanel({ stats, loading }) {
  if (loading || !stats) {
    return (
      <aside className="scan-stats-panel" aria-busy="true">
        <div className="skeleton-card" style={{ height: 120 }} />
        <div className="skeleton-card" style={{ height: 160 }} />
        <div className="skeleton-card" style={{ height: 200 }} />
      </aside>
    );
  }

  const total = stats.total || 0;
  const last30d = stats.last_30d || 0;

  return (
    <aside className="scan-stats-panel">
      <section aria-labelledby="stats-activity">
        <h3 id="stats-activity" className="stats-section-title">Activity</h3>
        <div className="stats-timeline">
          <TimelineCell label="24h" value={stats.last_24h} />
          <TimelineCell label="7d" value={stats.last_7d} />
          <TimelineCell label="30d" value={stats.last_30d} />
        </div>
        <p className="muted" style={{ fontSize: 'var(--fs-xs)', marginTop: 'var(--sp-3)', textAlign: 'center' }}>
          {stats.unique_staff || 0} unique staff scanned · {total} all-time
        </p>
      </section>

      <section aria-labelledby="stats-breakdown">
        <h3 id="stats-breakdown" className="stats-section-title">Last 30 days by result</h3>
        <div className="stats-breakdown">
          <BreakdownRow
            state={getVerifyState('verified')}
            count={stats.by_result?.verified || 0}
            total={last30d}
          />
          <BreakdownRow
            state={getVerifyState('under_review')}
            count={stats.by_result?.under_review || 0}
            total={last30d}
          />
          <BreakdownRow
            state={getVerifyState('no_longer_employed')}
            count={stats.by_result?.no_longer_employed || 0}
            total={last30d}
          />
          <BreakdownRow
            state={getVerifyState('unknown')}
            count={stats.by_result?.unknown || 0}
            total={last30d}
            warn  /* Unknown scans are a security signal */
          />
        </div>
        {(stats.by_result?.unknown || 0) > 0 && (
          <p style={{ fontSize: 'var(--fs-xs)', marginTop: 'var(--sp-3)', color: 'var(--id-red-d)' }}>
            <strong>Unknown scans detected.</strong>{' '}
            Could be typos on printed cards — or someone's distributed a fake QR.
          </p>
        )}
      </section>

      <section aria-labelledby="stats-top">
        <h3 id="stats-top" className="stats-section-title">Top staff (30d)</h3>
        {(stats.top_staff || []).length === 0 ? (
          <p className="muted" style={{ fontSize: 'var(--fs-sm)' }}>No scans yet.</p>
        ) : (
          <ol className="top-staff-list">
            {(stats.top_staff || []).slice(0, 5).map((s, i) => (
              <li key={s.staff_id}>
                <Link to={`/dashboard/staff/${s.staff_id}`} className="top-staff-row">
                  <span className="top-staff-rank">{i + 1}</span>
                  <span className="top-staff-name">
                    {`${s.first_name || ''} ${s.last_name || ''}`.trim() || s.slug}
                  </span>
                  <span className="top-staff-count">{s.scan_count} scans</span>
                </Link>
              </li>
            ))}
          </ol>
        )}
      </section>
    </aside>
  );
}

function TimelineCell({ label, value }) {
  return (
    <div className="stats-timeline-cell">
      <div className="stats-timeline-value">{value || 0}</div>
      <div className="stats-timeline-label">{label}</div>
    </div>
  );
}

function BreakdownRow({ state, count, total, warn }) {
  const percent = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="stats-breakdown-row">
      <span className="stats-breakdown-label" style={warn && count > 0 ? { color: 'var(--id-red-d)', fontWeight: 600 } : undefined}>
        <span
          aria-hidden="true"
          style={{
            width: 8, height: 8, borderRadius: '50%', background: state.color,
            display: 'inline-block',
          }}
        />
        {state.label}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
        <span className="stats-breakdown-bar" aria-hidden="true">
          <span
            className="stats-breakdown-bar-fill"
            style={{
              width: `${percent}%`,
              background: state.color,
            }}
          />
        </span>
        <span style={{ fontVariantNumeric: 'tabular-nums', minWidth: 28, textAlign: 'right' }}>
          {count}
        </span>
      </span>
    </div>
  );
}
